import { useCallback, useEffect, useRef, useState } from 'react';
import { Position, POSITION } from 'models/Position';
import { getScrollParent } from 'utils';
import useMatchMedia from 'hooks/useMatchMedia';

const ARROW_WIDTH = 12;
const MOBILE_HORIZONTAL_OFFSET = 16;
const VIEWPORT_SIDE_OFFSET = 16;

const getClampedLeft = (left: number, maxLeft: number) => {
  const safeMaxLeft = Math.max(maxLeft - VIEWPORT_SIDE_OFFSET, 0);

  return Math.min(Math.max(left, VIEWPORT_SIDE_OFFSET), safeMaxLeft);
};

const getVerticalTop = (
  position: Position,
  anchorRect: DOMRect,
  contentHeight: number,
) =>
  position === POSITION.BOTTOM
    ? anchorRect.bottom + window.scrollY + ARROW_WIDTH
    : anchorRect.top + window.scrollY - contentHeight - ARROW_WIDTH;

const getHorizontalPosition = (
  anchorRect: DOMRect,
  contentWidth: number,
  contentHeight: number,
  hasSpaceOnRight: boolean,
) =>
  ({
    left: hasSpaceOnRight
      ? anchorRect.left + anchorRect.width + ARROW_WIDTH
      : anchorRect.left - contentWidth - ARROW_WIDTH,
    top:
      anchorRect.y + window.scrollY + (anchorRect.height - contentHeight) / 2,
    bannerPosition: hasSpaceOnRight ? POSITION.RIGHT : POSITION.LEFT,
  } as const);

const useFloatingBannerPositioning = ({
  onHover,
  preferredPosition,
  setIsInViewport,
}: {
  onHover?: boolean;
  preferredPosition?: Position;
  setIsInViewport: (value: boolean) => void;
}) => {
  const [position, setPosition] = useState(null);
  const bannerPosition = useRef<Position>(POSITION.RIGHT);

  const animationFrameRef = useRef<number>(null);
  const currentPositionRef = useRef(null);

  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isPhablet = useMatchMedia('isPhablet');

  const updatePosition = useCallback(
    (left: number, top: number) => {
      const currentPosition = currentPositionRef.current;

      if (currentPosition?.left === left && currentPosition?.top === top)
        return;

      currentPositionRef.current = { left, top };

      const content = contentRef.current;

      if (content && position) {
        content.style.left = `${left}px`;
        content.style.top = `${top}px`;
        return;
      }

      setPosition({ left, top });
    },
    [position],
  );

  const getPreferredVerticalPosition = useCallback(
    (hasSpaceBelow: boolean, hasSpaceAbove: boolean) =>
      preferredPosition === POSITION.BOTTOM &&
      hasSpaceBelow &&
      (!isPhablet || hasSpaceAbove)
        ? POSITION.BOTTOM
        : preferredPosition === POSITION.TOP && hasSpaceAbove
        ? POSITION.TOP
        : null,
    [isPhablet, preferredPosition],
  );

  const getAutoVerticalPosition = useCallback(
    (
      hasSpaceOnRightBelow: boolean,
      hasSpaceAbove: boolean,
      hasSpaceBelow: boolean,
    ) =>
      isPhablet && !hasSpaceOnRightBelow && hasSpaceAbove
        ? POSITION.TOP
        : hasSpaceBelow
        ? POSITION.BOTTOM
        : POSITION.TOP,
    [isPhablet],
  );

  const getDefaultVerticalPosition = useCallback(
    (
      position: Position,
      anchorRect: DOMRect,
      contentWidth: number,
      centeredLeft: number,
      contentHeight: number,
      hasSpaceOnRightBelow: boolean,
      hasSpaceOnLeftCentered: boolean,
    ) => {
      const shouldBeLeft = isPhablet || !hasSpaceOnLeftCentered;

      const { left, top, bottom, width } = anchorRect || {};

      return {
        left: shouldBeLeft
          ? hasSpaceOnRightBelow
            ? left - MOBILE_HORIZONTAL_OFFSET
            : left + width - contentWidth + MOBILE_HORIZONTAL_OFFSET
          : centeredLeft,
        top:
          position === POSITION.BOTTOM
            ? bottom + window.scrollY + ARROW_WIDTH
            : top + window.scrollY - contentHeight - ARROW_WIDTH,
      };
    },
    [isPhablet],
  );

  const calculateAndUpdateBannerPosition = useCallback(
    (anchorRect: DOMRect, contentRect: DOMRect) => {
      const viewportWidth = window.innerWidth;
      const { width: contentWidth, height: contentHeight } = contentRect;

      if (!contentWidth || !contentHeight) return false;

      const spaceOnRight = viewportWidth - (anchorRect.x + anchorRect.width);

      const hasSpaceOnRight = spaceOnRight >= contentWidth + ARROW_WIDTH;
      const hasSpaceOnLeft = anchorRect.x >= contentWidth + ARROW_WIDTH;
      const hasSpaceBelow =
        window.innerHeight -
          (anchorRect.bottom + contentHeight + ARROW_WIDTH) >=
        0;
      const hasSpaceAbove = anchorRect.top - contentHeight - ARROW_WIDTH >= 0;

      const centeredTop =
        anchorRect.top +
        window.scrollY +
        anchorRect.height / 2 -
        contentHeight / 2;

      const hasSpaceAboveCentered = centeredTop >= 0;
      const hasSpaceBelowCentered =
        centeredTop + contentHeight <= window.innerHeight + window.scrollY;

      const centeredLeft =
        anchorRect.left +
        window.scrollX +
        anchorRect.width / 2 -
        contentWidth / 2;

      const hasSpaceOnLeftCentered = centeredLeft >= 0;
      const hasSpaceOnRightBelow =
        spaceOnRight + anchorRect.width >= contentWidth;

      const canUseHorizontalPosition =
        (hasSpaceOnLeft || hasSpaceOnRight) &&
        hasSpaceAboveCentered &&
        hasSpaceBelowCentered;

      const preferredVerticalPosition = getPreferredVerticalPosition(
        hasSpaceBelow,
        hasSpaceAbove,
      );

      if (preferredVerticalPosition || !canUseHorizontalPosition) {
        const autoVerticalPosition = getAutoVerticalPosition(
          hasSpaceOnRightBelow,
          hasSpaceAbove,
          hasSpaceBelow,
        );

        const verticalPosition =
          preferredVerticalPosition || autoVerticalPosition;

        bannerPosition.current = verticalPosition;

        if (!preferredVerticalPosition) {
          const { left, top } = getDefaultVerticalPosition(
            verticalPosition,
            anchorRect,
            contentWidth,
            centeredLeft,
            contentHeight,
            hasSpaceOnRightBelow,
            hasSpaceOnLeftCentered,
          );

          updatePosition(
            getClampedLeft(left, viewportWidth - contentWidth),
            top,
          );
          return true;
        }

        const clampedCenteredLeft = getClampedLeft(
          centeredLeft,
          viewportWidth - contentWidth,
        );

        updatePosition(
          clampedCenteredLeft,
          getVerticalTop(verticalPosition, anchorRect, contentHeight),
        );
        return true;
      }

      const {
        left,
        top,
        bannerPosition: nextBannerPosition,
      } = getHorizontalPosition(
        anchorRect,
        contentWidth,
        contentHeight,
        hasSpaceOnRight,
      );

      bannerPosition.current = nextBannerPosition;
      updatePosition(getClampedLeft(left, viewportWidth - contentWidth), top);
      return true;
    },
    [
      getAutoVerticalPosition,
      getDefaultVerticalPosition,
      getPreferredVerticalPosition,
      updatePosition,
    ],
  );

  const updateIfAnchorInViewport = useCallback(
    (anchor: HTMLElement) => {
      if (onHover) return;

      const scrollContainer = getScrollParent(anchor);
      const anchorRect = anchor.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      const inXViewport =
        anchorRect.right > containerRect.left &&
        anchorRect.left < containerRect.right;

      const inYViewport =
        anchorRect.bottom > containerRect.top &&
        anchorRect.top < containerRect.bottom;

      setIsInViewport(inXViewport && inYViewport);
    },
    [onHover, setIsInViewport],
  );

  const resetAndRetry = useCallback((retry: () => void) => {
    currentPositionRef.current = null;
    setPosition(null);
    retry();
  }, []);

  const updateBannerPosition = useCallback(
    (shouldRetry = true) => {
      const scheduleRetry = () => {
        if (!shouldRetry) return;

        if (animationFrameRef.current) {
          window.cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = window.requestAnimationFrame(() => {
          animationFrameRef.current = null;
          updateBannerPosition(false);
        });
      };

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = null;

        const anchor = anchorRef.current;
        const content = contentRef.current;

        if (!anchor || !content) {
          resetAndRetry(scheduleRetry);
          return;
        }

        updateIfAnchorInViewport(anchor);

        const anchorRect = anchor.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        const hasPosition = calculateAndUpdateBannerPosition(
          anchorRect,
          contentRect,
        );

        if (!hasPosition) {
          resetAndRetry(scheduleRetry);
        }
      });
    },
    [calculateAndUpdateBannerPosition, resetAndRetry, updateIfAnchorInViewport],
  );

  useEffect(
    () => () => {
      if (animationFrameRef.current)
        window.cancelAnimationFrame(animationFrameRef.current);
    },
    [],
  );

  return {
    position,
    bannerPosition,
    anchorRef,
    contentRef,
    updateBannerPosition,
  };
};

export default useFloatingBannerPositioning;
